import React, { useState, useEffect } from 'react';
import { X, GripVertical, Trash2, Plus, Video, Clock, Save, ArrowLeft, Folder, ChevronRight, Home, Shuffle, List, Play, Square } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';

function PlaylistEditor({ playlistId, onBack, user }) {
  const [playlist, setPlaylist] = useState(null);
  const [items, setItems] = useState([]);
  const [availableVods, setAvailableVods] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([{ id: null, name: 'Todos los videos' }]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedVods, setSelectedVods] = useState(new Set());
  const [draggedItem, setDraggedItem] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchPlaylist = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/playlists/${playlistId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPlaylist(response.data.playlist);
        setItems(response.data.playlist.items || []);
        setLoading(false);
      } catch (error) {
        toast.error('Error al cargar playlist');
        setLoading(false);
      }
    };
    fetchPlaylist();
  }, [playlistId]);

  useEffect(() => {
    const fetchAvailableVods = async () => {
      try {
        const token = localStorage.getItem('token');
        const params = currentFolder ? `?folder_id=${currentFolder}` : '?folder_id=null';
        const response = await axios.get(`/api/vod${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAvailableVods(response.data.vods || []);
      } catch (error) {
        console.error('Error al cargar VODs disponibles:', error);
      }
    };

    const fetchFolders = async () => {
      try {
        const token = localStorage.getItem('token');
        const params = currentFolder ? `?parent_id=${currentFolder}` : '';
        const response = await axios.get(`/api/vod/folders${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFolders(response.data.folders || []);
      } catch (error) {
        console.error('Error al cargar carpetas:', error);
      }
    };

    if (showAddModal) {
      fetchAvailableVods();
      fetchFolders();
    }
  }, [showAddModal, currentFolder]);

  const handleOpenFolder = (folder) => {
    setCurrentFolder(folder.id);
    setBreadcrumb([...breadcrumb, { id: folder.id, name: folder.name }]);
  };

  const handleBreadcrumbClick = (index) => {
    const newBreadcrumb = breadcrumb.slice(0, index + 1);
    setBreadcrumb(newBreadcrumb);
    setCurrentFolder(newBreadcrumb[newBreadcrumb.length - 1].id);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setSelectedVods(new Set());
    setCurrentFolder(null);
    setBreadcrumb([{ id: null, name: 'Todos los videos' }]);
  };

  const toggleVodSelection = (vod) => {
    setSelectedVods(prev => {
      const next = new Set(prev);
      if (next.has(vod.id)) {
        next.delete(vod.id);
      } else {
        next.add(vod.id);
      }
      return next;
    });
  };

  const handleAddSelectedVods = () => {
    const vodsToAdd = availableVods.filter(v => selectedVods.has(v.id));
    const newItems = vodsToAdd.map((vod, i) => ({
      id: Date.now() + i,
      vod_id: vod.id,
      title: vod.title,
      duration: vod.duration || 0,
      order: items.length + i
    }));
    setItems([...items, ...newItems]);
    toast.success(`${vodsToAdd.length} video${vodsToAdd.length !== 1 ? 's' : ''} agregado${vodsToAdd.length !== 1 ? 's' : ''} a la playlist`);
    handleCloseModal();
  };

  const handleRemoveItem = (itemId) => {
    setItems(items.filter(item => item.id !== itemId));
    toast.success('Video eliminado de la playlist');
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetItem.id) return;

    const draggedIndex = items.findIndex(item => item.id === draggedItem.id);
    const targetIndex = items.findIndex(item => item.id === targetItem.id);

    const newItems = [...items];
    newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, draggedItem);

    setItems(newItems.map((item, index) => ({ ...item, order: index })));
    setDraggedItem(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/playlists/${playlistId}`, {
        items,
        shuffle_mode: playlist.shuffle_mode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Playlist guardada exitosamente');
      onBack();
    } catch (error) {
      console.error('Error al guardar playlist:', error);
      toast.error('Error al guardar playlist');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleShuffleMode = () => {
    setPlaylist({ ...playlist, shuffle_mode: !playlist.shuffle_mode });
    toast.info(playlist.shuffle_mode ? 'Modo: En orden' : 'Modo: Aleatorio');
  };

  const handleToggleActive = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/playlists/${playlistId}`, {
        is_active: !playlist.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlaylist({ ...playlist, is_active: !playlist.is_active });
      toast.success(playlist.is_active ? 'Playlist desactivada' : 'Playlist activada');
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const formatDurationPrecise = (seconds) => {
    if (!seconds) return '00:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hrs > 0) {
      return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const getTotalDuration = () => {
    const total = items.reduce((sum, item) => sum + (item.duration || 0), 0);
    const hours = Math.floor(total / 3600);
    const mins = Math.floor((total % 3600) / 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-accent-purple/30 border-t-accent-purple rounded-full animate-spin"></div>
          <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-accent-indigo rounded-full animate-spin" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8 lg:p-10 min-h-screen max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button className="btn-glass group flex items-center gap-2"
                  onClick={onBack}>
            <ArrowLeft size={20} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Volver
          </button>
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-2 text-white">
              {playlist.name}
            </h2>
            <p className="text-gray-400 text-sm">
              {playlist.description || 'Sin descripción'}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button className={`btn-primary-gradient ${playlist.is_active
                         ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500'
                         : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500'}`}
                  onClick={handleToggleActive}>
            {playlist.is_active ? 'Desactivar' : 'Activar'}
          </button>
          <button className="btn-primary-gradient disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  onClick={handleSave}
                  disabled={saving}>
            <Save size={20} />
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="stat-card-premium group">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="text-sm text-gray-400 mb-2 font-medium">Videos</div>
                <div className="text-3xl font-bold text-white mb-3 transition-all duration-300 group-hover:text-accent-purple">
                  {items.length}
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-indigo flex items-center justify-center shadow-lg shadow-accent-purple/25 transition-all duration-300 group-hover:shadow-accent-purple/40">
                <Video size={24} className="text-white" />
              </div>
            </div>
          </div>

          <div className="stat-card-premium group">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="text-sm text-gray-400 mb-2 font-medium">Duración Total</div>
                <div className="text-3xl font-bold text-white mb-3 transition-all duration-300 group-hover:text-accent-indigo">
                  {getTotalDuration()}
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-indigo to-blue-500 flex items-center justify-center shadow-lg shadow-accent-indigo/25 transition-all duration-300 group-hover:shadow-accent-indigo/40">
                <Clock size={24} className="text-white" />
              </div>
            </div>
          </div>

          <div className="stat-card-premium group">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="text-sm text-gray-400 mb-2 font-medium">Estado</div>
                <div className="flex items-center gap-2">
                  {playlist.is_active ? (
                    <span className="pill-glass pill-online">
                      <div className="w-2.5 h-2.5 bg-status-live rounded-full animate-pulse shadow-lg shadow-status-live/50"></div>
                      <span className="font-semibold">Activa</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 backdrop-blur-sm">
                      Inactiva
                    </span>
                  )}
                </div>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg transition-all duration-300 ${
                playlist.is_active
                  ? 'bg-gradient-to-br from-status-live to-red-600 shadow-status-live/25 group-hover:shadow-status-live/40'
                  : 'bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-yellow-500/25 group-hover:shadow-yellow-500/40'
              }`}>
                {playlist.is_active ? <Play size={24} className="text-white" /> : <Square size={24} className="text-white" />}
              </div>
            </div>
          </div>
        </div>

      {/* Playback Mode */}
      <div className="glass-card mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-purple/20 to-accent-indigo/20 border border-accent-purple/30 flex items-center justify-center flex-shrink-0">
            {playlist.shuffle_mode ? <Shuffle size={18} className="text-accent-purple" /> : <List size={18} className="text-accent-purple" />}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">Modo de Reproducción</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {playlist.shuffle_mode ? 'Los videos se reproducen en orden aleatorio' : 'Los videos se reproducen en el orden de la lista'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
              !playlist.shuffle_mode ? 'btn-primary-gradient btn-sm' : 'btn-glass btn-sm'
            }`}
            onClick={() => setPlaylist({ ...playlist, shuffle_mode: false })}
          >
            <List size={15} /> En Orden
          </button>
          <button
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
              playlist.shuffle_mode ? 'btn-primary-gradient btn-sm' : 'btn-glass btn-sm'
            }`}
            onClick={() => setPlaylist({ ...playlist, shuffle_mode: true })}
          >
            <Shuffle size={15} /> Aleatorio
          </button>
        </div>
      </div>

      {/* Items List Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-white">
            Cola de reproducción
          </h3>
          {items.length > 0 && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-accent-purple/15 text-accent-purple border border-accent-purple/25 font-semibold">
              {items.length} video{items.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button className="btn-primary-gradient btn-sm group flex items-center gap-1.5"
                onClick={() => setShowAddModal(true)}>
          <Plus size={15} className="transition-transform duration-300 group-hover:rotate-90" />
          Agregar Video
        </button>
      </div>

      {/* Items List */}
      {items.length === 0 ? (
        <div className="glass-card text-center py-16">
          <div className="w-20 h-20 mx-auto mb-6 rounded-xl bg-gradient-to-br from-accent-purple/20 to-accent-indigo/20 backdrop-blur-sm flex items-center justify-center border border-accent-purple/30">
            <Video size={40} className="text-accent-purple opacity-60" />
          </div>
          <h3 className="text-xl font-bold mb-2 text-white">
            Cola vacía
          </h3>
          <p className="text-gray-400 text-sm mb-6">
            Agrega videos para comenzar la playlist
          </p>
          <button className="btn-primary-gradient mx-auto group flex items-center gap-2"
                  onClick={() => setShowAddModal(true)}>
            <Plus size={18} className="transition-transform duration-300 group-hover:rotate-90" />
            Agregar Primer Video
          </button>
        </div>
      ) : (
        <div className="glass-card p-0 overflow-hidden">
          {items.map((item, index) => (
            <div
              key={item.id}
              className={`flex items-center gap-3 px-4 py-3.5 border-b border-white/5 last:border-0 cursor-move
                         transition-all duration-200 group
                         hover:bg-accent-purple/5
                         ${draggedItem?.id === item.id ? 'bg-accent-purple/10 border-l-2 border-l-accent-purple' : ''}`}
              draggable
              onDragStart={(e) => handleDragStart(e, item)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, item)}
            >
              <GripVertical size={16} className="text-gray-600 group-hover:text-accent-purple/60 transition-colors cursor-grab active:cursor-grabbing flex-shrink-0" />
              <div className="w-8 h-8 bg-gradient-to-br from-accent-purple/80 to-accent-indigo/80 rounded-lg flex items-center justify-center font-bold text-xs text-white flex-shrink-0">
                {index + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate group-hover:text-accent-purple/90 transition-colors">
                  {item.title}
                </div>
                <div className="flex items-center gap-1.5 text-gray-500 text-xs mt-0.5">
                  <Clock size={11} />
                  <span>{formatDurationPrecise(item.duration)}</span>
                </div>
              </div>
              <button
                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/30 flex items-center justify-center transition-all text-gray-500 hover:text-red-400 flex-shrink-0"
                onClick={() => handleRemoveItem(item.id)}
                title="Eliminar"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {/* Footer with total */}
          <div className="flex items-center justify-between px-4 py-3 bg-white/3 border-t border-white/8">
            <span className="text-xs text-gray-500">{items.length} videos · duración total</span>
            <span className="text-sm font-semibold text-white">{getTotalDuration()}</span>
          </div>
        </div>
      )}

      {/* Add VOD Modal — z-[200] para estar sobre el sidebar */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[200] p-4 animate-fade-in"
             onClick={handleCloseModal}>
          <div className="glass-card w-full max-w-3xl flex flex-col animate-slide-up border-2 border-accent-purple/30 shadow-2xl shadow-accent-purple/20"
               style={{ maxHeight: 'min(85vh, 700px)' }}
               onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-purple/30 to-accent-indigo/30 flex items-center justify-center border border-accent-purple/40">
                  <Plus size={20} className="text-accent-purple" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Agregar Videos</h2>
                  <p className="text-xs text-gray-400">Selecciona uno o varios videos</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {selectedVods.size > 0 && (
                  <span className="text-sm font-semibold text-accent-purple bg-accent-purple/10 border border-accent-purple/30 px-3 py-1 rounded-full">
                    {selectedVods.size} seleccionado{selectedVods.size !== 1 ? 's' : ''}
                  </span>
                )}
                <button onClick={handleCloseModal}
                        className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 hover:bg-red-500/20 hover:border-red-500/30 text-gray-400 hover:text-red-400 flex items-center justify-center transition-all">
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="flex items-center gap-2 flex-wrap pb-3 mb-3 border-b border-white/10 flex-shrink-0">
              {breadcrumb.map((item, index) => (
                <React.Fragment key={index}>
                  <button
                    onClick={() => handleBreadcrumbClick(index)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                      index === breadcrumb.length - 1
                        ? 'bg-gradient-to-r from-accent-purple to-accent-indigo text-white shadow-lg shadow-accent-purple/30'
                        : 'bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10'
                    }`}>
                    {index === 0 && <Home size={13} />}
                    {item.name}
                  </button>
                  {index < breadcrumb.length - 1 && <ChevronRight size={13} className="text-gray-600" />}
                </React.Fragment>
              ))}
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto min-h-0 pr-1">
              {/* Folders */}
              {folders.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Carpetas</h4>
                  <div className="flex flex-col gap-1.5">
                    {folders.map((folder) => (
                      <div key={folder.id}
                           className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:border-yellow-500/30 cursor-pointer group transition-all"
                           onClick={() => handleOpenFolder(folder)}>
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center border border-yellow-500/30 flex-shrink-0">
                          <Folder size={18} className="text-yellow-500" />
                        </div>
                        <span className="font-medium text-white group-hover:text-yellow-400 transition-colors flex-1">{folder.name}</span>
                        <ChevronRight size={15} className="text-gray-600 group-hover:text-yellow-500 transition-colors" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Videos */}
              {availableVods.length === 0 && folders.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-br from-accent-purple/20 to-accent-indigo/20 flex items-center justify-center border border-accent-purple/30">
                    <Video size={32} className="text-accent-purple opacity-50" />
                  </div>
                  <p className="text-gray-400 text-sm">No hay videos en esta ubicación</p>
                </div>
              ) : availableVods.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Videos ({availableVods.length})
                    </h4>
                    {availableVods.length > 1 && (
                      <button
                        className="text-xs text-accent-purple hover:text-accent-indigo transition-colors"
                        onClick={() => {
                          if (selectedVods.size === availableVods.length) {
                            setSelectedVods(new Set());
                          } else {
                            setSelectedVods(new Set(availableVods.map(v => v.id)));
                          }
                        }}>
                        {selectedVods.size === availableVods.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                      </button>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {availableVods.map((vod) => {
                      const isSelected = selectedVods.has(vod.id);
                      return (
                        <div
                          key={vod.id}
                          className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all duration-150 ${
                            isSelected
                              ? 'bg-accent-purple/10 border-accent-purple/50'
                              : 'bg-white/5 border-white/10 hover:border-accent-purple/30 hover:bg-white/8'
                          }`}
                          onClick={() => toggleVodSelection(vod)}>
                          {/* Checkbox */}
                          <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2 transition-all ${
                            isSelected
                              ? 'bg-accent-purple border-accent-purple'
                              : 'border-gray-600 bg-transparent'
                          }`}>
                            {isSelected && (
                              <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                                <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                          {/* Icon */}
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
                            isSelected
                              ? 'bg-gradient-to-br from-accent-purple to-accent-indigo'
                              : 'bg-gradient-to-br from-accent-purple/20 to-accent-indigo/20 border border-accent-purple/30'
                          }`}>
                            <Video size={18} className={isSelected ? 'text-white' : 'text-accent-purple'} />
                          </div>
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className={`font-medium text-sm truncate transition-colors ${isSelected ? 'text-white' : 'text-gray-200'}`}>
                              {vod.title}
                            </div>
                            <div className="flex items-center gap-3 mt-0.5">
                              {vod.duration > 0 && (
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                  <Clock size={11} />
                                  {formatDurationPrecise(vod.duration)}
                                </span>
                              )}
                              {vod.formatted_size && (
                                <span className="text-xs text-accent-emerald">{vod.formatted_size}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 pt-4 mt-3 border-t border-white/10 flex-shrink-0">
              <button onClick={handleCloseModal}
                      className="glass-card px-5 py-2.5 text-sm hover:border-white/20 transition-all">
                Cancelar
              </button>
              <button
                onClick={handleAddSelectedVods}
                disabled={selectedVods.size === 0}
                className="btn-primary-gradient disabled:opacity-40 disabled:cursor-not-allowed">
                <Plus size={16} />
                {selectedVods.size === 0
                  ? 'Selecciona videos'
                  : `Agregar ${selectedVods.size} video${selectedVods.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlaylistEditor;
